<script setup>
/* global axios */
// Box "Mensagens Agendadas" — lista agendadas do cliente (enviadas/não enviadas) e
// agenda novas: texto + anexo, ou template WhatsApp (só p/ inbox API Oficial Meta).
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t, locale } = useI18n();
const dtLocale = computed(() => locale.value.replace('_', '-'));

const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const currentChat = useMapGetter('getSelectedChat');
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(
  () => inboxGetter.value(currentChat.value?.inbox_id) || {}
);
const isWhatsapp = computed(
  () => inbox.value?.channel_type === 'Channel::Whatsapp'
);
const templates = computed(() =>
  (inbox.value?.message_templates || []).filter(
    tpl => (tpl.status || 'APPROVED').toUpperCase() === 'APPROVED'
  )
);

const list = ref([]);
const loading = ref(true);
const showForm = ref(false);
const search = ref('');
const page = ref(1);
const perPage = 3;
// O box é um espaço apertado do painel lateral: só o que AINDA VAI ACONTECER
// fica na lista. Disparadas, falhas e canceladas viram histórico recolhido —
// continuam no banco, mas não poluem a tela do dia a dia (issue #50).
const showHistory = ref(false);
const pending = computed(() => list.value.filter(m => m.status === 'pending'));
const history = computed(() => list.value.filter(m => m.status !== 'pending'));
const visible = computed(() =>
  showHistory.value ? history.value : pending.value
);
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return visible.value;
  return visible.value.filter(m =>
    `${m.content || ''} ${m.template_name || ''}`.toLowerCase().includes(q)
  );
});
const totalPages = computed(() =>
  Math.max(1, Math.ceil(filtered.value.length / perPage))
);
const paged = computed(() =>
  filtered.value.slice((page.value - 1) * perPage, page.value * perPage)
);
watch([search, list, showHistory], () => {
  if (page.value > totalPages.value) page.value = 1;
});
const mode = ref('text'); // text | template
const msg = ref('');
const at = ref('');
const saving = ref(false);
const files = ref([]);
const fileInput = ref(null);
const selTplName = ref('');
const tplParams = ref([]);
const tplHeaderUrl = ref('');

const selTpl = computed(
  () => templates.value.find(tpl => tpl.name === selTplName.value) || null
);
const tplBody = computed(
  () => selTpl.value?.components?.find(c => c.type === 'BODY')?.text || ''
);
const tplParamCount = computed(
  () => (tplBody.value.match(/\{\{\d+\}\}/g) || []).length
);
const tplHeader = computed(() =>
  selTpl.value?.components?.find(c => c.type === 'HEADER')
);
const tplHeaderMedia = computed(() =>
  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(tplHeader.value?.format)
    ? tplHeader.value.format
    : null
);

const label = s =>
  ({
    pending: t('KANBAN.scheduled.status.pending'),
    sent: t('KANBAN.scheduled.status.sent'),
    failed: t('KANBAN.scheduled.status.failed'),
    cancelled: t('KANBAN.scheduled.status.cancelled'),
  })[s] || s;
const cls = s =>
  ({
    pending: 'text-amber-700 dark:text-amber-300',
    sent: 'text-green-700 dark:text-green-300',
    failed: 'text-red-700 dark:text-red-300',
    cancelled: 'text-n-slate-10',
  })[s] || '';
const fmt = iso => (iso ? new Date(iso).toLocaleString(dtLocale.value) : '');

const load = async () => {
  loading.value = true;
  try {
    list.value =
      (
        await axios.get(`${base}/scheduled_messages`, {
          params: { conversation_id: props.conversationId },
        })
      ).data.data || [];
  } catch (e) {
    list.value = [];
  }
  loading.value = false;
};
const onPickTpl = () => {
  tplParams.value = Array(tplParamCount.value).fill('');
  tplHeaderUrl.value = '';
};
const onFiles = e => {
  files.value = Array.from(e.target.files || []);
};

const buildPreview = () => {
  let p = tplBody.value || selTpl.value?.name || '';
  tplParams.value.forEach((v, i) => {
    p = p.replace(`{{${i + 1}}}`, v || `{{${i + 1}}}`);
  });
  return p;
};

const add = async () => {
  if (!at.value) return;
  const fd = new FormData();
  fd.append('conversation_id', props.conversationId);
  fd.append('scheduled_at', new Date(at.value).toISOString());
  if (mode.value === 'template' && selTpl.value) {
    const processed = {};
    tplParams.value.forEach((v, i) => {
      processed[i + 1] = v;
    });
    const tp = {
      name: selTpl.value.name,
      language: selTpl.value.language,
      category: selTpl.value.category,
      namespace: selTpl.value.namespace || null,
      processed_params: processed,
    };
    if (tplHeaderMedia.value && tplHeaderUrl.value)
      tp.processed_params.header = { media_url: tplHeaderUrl.value };
    fd.append('message_type', 'template');
    fd.append('template_name', selTpl.value.name);
    fd.append('template_params', JSON.stringify(tp));
    fd.append('content', buildPreview());
  } else {
    if (!msg.value.trim() && !files.value.length) return;
    fd.append('message_type', 'text');
    fd.append(
      'content',
      msg.value.trim() || t('KANBAN.scheduled.attachmentFallback')
    );
    files.value.forEach(f => fd.append('attachments[]', f));
  }
  saving.value = true;
  try {
    await axios.post(`${base}/scheduled_messages`, fd);
    msg.value = '';
    at.value = '';
    files.value = [];
    selTplName.value = '';
    tplParams.value = [];
    tplHeaderUrl.value = '';
    if (fileInput.value) fileInput.value.value = '';
    showForm.value = false;
    await load();
  } finally {
    saving.value = false;
  }
};
const cancel = async m => {
  await axios.delete(`${base}/scheduled_messages/${m.id}`);
  await load();
};
const editId = ref(null);
const editContent = ref('');
const editAt = ref('');
const toLocalInput = iso => {
  const d = new Date(iso);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
// Templates de API oficial (Meta) não podem ser editados — a Meta só permite
// criar/remover (feito nas configs do inbox). Só dá para editar se NÃO for um
// inbox oficial OU se a mensagem não for do tipo template.
const canEdit = m => !isWhatsapp.value || m.message_type !== 'template';
const startEdit = m => {
  editId.value = m.id;
  editContent.value = m.content;
  editAt.value = m.scheduled_at ? toLocalInput(m.scheduled_at) : '';
};
const saveEdit = async m => {
  await axios.patch(`${base}/scheduled_messages/${m.id}`, {
    content: editContent.value,
    scheduled_at: editAt.value ? new Date(editAt.value).toISOString() : null,
  });
  editId.value = null;
  await load();
};

watch(() => props.conversationId, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-2 px-3.5 pt-2.5 pb-3.5">
    <div v-if="loading" class="text-xs text-n-slate-10">
      {{ t('KANBAN.scheduled.loading') }}
    </div>
    <template v-else>
      <!-- 1) Botão / formulário no topo -->
      <div
        v-if="showForm"
        class="flex flex-col gap-1.5 pt-1.5 border-t border-dashed border-n-weak"
      >
        <div
          v-if="isWhatsapp"
          class="flex gap-1 p-[3px] rounded-lg bg-n-slate-5"
        >
          <button
            class="flex-1 p-1.5 text-xs rounded-md text-n-slate-10"
            :class="{
              'bg-n-background text-n-brand font-semibold shadow-sm':
                mode === 'text',
            }"
            @click="mode = 'text'"
          >
            {{ t('KANBAN.scheduled.modeText') }}
          </button>
          <button
            class="flex-1 p-1.5 text-xs rounded-md text-n-slate-10"
            :class="{
              'bg-n-background text-n-brand font-semibold shadow-sm':
                mode === 'template',
            }"
            @click="mode = 'template'"
          >
            {{ t('KANBAN.scheduled.modeTemplate') }}
          </button>
        </div>

        <template v-if="mode === 'template' && isWhatsapp">
          <select
            v-model="selTplName"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            @change="onPickTpl"
          >
            <option value="">{{ t('KANBAN.scheduled.selectTemplate') }}</option>
            <option v-for="t in templates" :key="t.name" :value="t.name">
              {{ t.name }} ({{ t.language }})
            </option>
          </select>
          <template v-if="selTpl">
            <p
              class="text-[0.6875rem] text-n-slate-10 bg-n-alpha-1 p-1.5 rounded-lg"
            >
              {{ tplBody }}
            </p>
            <input
              v-for="(p, i) in tplParams"
              :key="i"
              v-model="tplParams[i]"
              :placeholder="
                t('KANBAN.scheduled.variablePlaceholder', {
                  index: `{{${i + 1}}}`,
                })
              "
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
            <input
              v-if="tplHeaderMedia"
              v-model="tplHeaderUrl"
              :placeholder="
                t('KANBAN.scheduled.mediaUrlPlaceholder', {
                  format: tplHeaderMedia,
                })
              "
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
          </template>
        </template>
        <template v-else>
          <textarea
            v-model="msg"
            rows="2"
            :placeholder="t('KANBAN.scheduled.messagePlaceholder')"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
          <label class="text-xs text-n-blue-11 cursor-pointer py-1">
            <input
              ref="fileInput"
              type="file"
              multiple
              class="hidden"
              @change="onFiles"
            />
            <svg
              class="inline w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
              />
            </svg>
            {{
              files.length
                ? t('KANBAN.scheduled.filesCount', { count: files.length })
                : t('KANBAN.scheduled.attachFile')
            }}
          </label>
        </template>

        <label class="text-xs text-n-slate-10">{{
          t('KANBAN.scheduled.sendAtLabel')
        }}</label>
        <input
          v-model="at"
          type="datetime-local"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
        <div class="flex gap-1.5">
          <button
            class="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="saving || !at"
            @click="add"
          >
            {{
              saving
                ? t('KANBAN.scheduled.scheduling')
                : t('KANBAN.scheduled.schedule')
            }}
          </button>
          <button
            class="px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1"
            @click="showForm = false"
          >
            {{ t('KANBAN.scheduled.cancel') }}
          </button>
        </div>
      </div>
      <button
        v-else
        class="w-full px-3 py-2 text-sm font-medium rounded-lg border border-n-blue-6 text-n-blue-11 bg-n-background hover:bg-n-alpha-1"
        @click="showForm = true"
      >
        {{ t('KANBAN.scheduled.scheduleMessage') }}
      </button>

      <!-- 2) Busca -->
      <input
        v-if="visible.length"
        v-model="search"
        :placeholder="t('KANBAN.scheduled.searchPlaceholder')"
        class="w-full mt-0.5 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
      />

      <!-- 3) Lista paginada (3 por página) -->
      <div v-if="!filtered.length" class="text-xs text-n-slate-10">
        <template v-if="visible.length">
          {{ t('KANBAN.scheduled.nothingFound') }}
        </template>
        <template v-else-if="showHistory">
          {{ t('KANBAN.scheduled.historyEmpty') }}
        </template>
        <template v-else>
          {{ t('KANBAN.scheduled.emptyPending') }}
        </template>
      </div>
      <div
        v-for="m in paged"
        :key="m.id"
        class="flex items-start gap-2 py-1.5 border-b border-n-weak last-of-type:border-b-0"
      >
        <div
          v-if="editId === m.id"
          class="flex flex-1 flex-col gap-1.5 pt-1.5 border-t border-dashed border-n-weak"
        >
          <textarea
            v-model="editContent"
            rows="2"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
          <label class="text-xs text-n-slate-10">{{
            t('KANBAN.scheduled.sendAtLabel')
          }}</label>
          <input
            v-model="editAt"
            type="datetime-local"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
          <div class="flex gap-1.5">
            <button
              class="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95"
              @click="saveEdit(m)"
            >
              {{ t('KANBAN.scheduled.save') }}
            </button>
            <button
              class="px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1"
              @click="editId = null"
            >
              {{ t('KANBAN.scheduled.cancel') }}
            </button>
          </div>
        </div>
        <template v-else>
          <div class="min-w-0">
            <div class="text-sm text-n-slate-12">
              <span
                v-if="m.message_type === 'template'"
                class="inline-block mr-1.5 px-1.5 text-[0.625rem] rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                >{{ t('KANBAN.scheduled.templateBadge') }}</span
              >{{ m.content }}
            </div>
            <div
              v-if="m.attachments && m.attachments.length"
              class="text-[0.6875rem] text-n-slate-10 mt-0.5"
            >
              <svg
                class="inline w-3 h-3"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                />
              </svg>
              {{ m.attachments.map(a => a.filename).join(', ') }}
            </div>
            <div class="text-[0.6875rem] text-n-slate-10 mt-0.5">
              <svg
                class="inline w-3 h-3"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
              {{ fmt(m.scheduled_at) }} ·
              <span :class="cls(m.status)">{{ label(m.status) }}</span>
            </div>
          </div>
          <span v-if="m.status === 'pending'" class="ml-auto flex gap-2">
            <button
              v-if="canEdit(m)"
              class="bg-transparent text-n-slate-10 text-xs hover:text-n-ruby-11"
              :title="t('KANBAN.scheduled.editTitle')"
              @click="startEdit(m)"
            >
              <svg
                class="inline w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"
                />
              </svg>
            </button>
            <button
              class="bg-transparent text-n-slate-10 text-xs hover:text-n-ruby-11"
              :title="t('KANBAN.scheduled.cancelTitle')"
              @click="cancel(m)"
            >
              <svg
                class="inline w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        </template>
      </div>

      <!-- 4) Paginação -->
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-center gap-3 pt-1"
      >
        <button
          class="w-7 h-[26px] rounded-lg border border-n-weak bg-n-background text-n-slate-11 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="page <= 1"
          @click="page--"
        >
          <svg
            class="inline w-3.5 h-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span class="text-xs text-n-slate-10"
          >{{ page }} / {{ totalPages }}</span
        >
        <button
          class="w-7 h-[26px] rounded-lg border border-n-weak bg-n-background text-n-slate-11 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="page >= totalPages"
          @click="page++"
        >
          <svg
            class="inline w-3.5 h-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      <!-- 5) Histórico (disparadas, falhas e canceladas) -->
      <button
        v-if="history.length || showHistory"
        class="self-start text-xs underline text-n-slate-10 hover:text-n-slate-12"
        @click="showHistory = !showHistory"
      >
        {{
          showHistory
            ? t('KANBAN.scheduled.hideHistory')
            : t('KANBAN.scheduled.history', { count: history.length })
        }}
      </button>
    </template>
  </div>
</template>
